import { useMemo, useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ModelRecord } from 'spraypaint/lib-esm/model';
import { useAddComment } from '@common/components/comments/hooks/useAddComment';
import DendreoSyncInvoiceRequest from '@spraypaint/dendreo/DendreoInvoiceSyncState';
import { IgnoreInvoiceModal } from '../components/IgnoreInvoiceModal';

type Params = {
  invoice: ModelRecord<DendreoSyncInvoiceRequest>;
};

export const useIgnoreInvoiceModal = ({ invoice }: Params) => {
  const invoiceRecord = invoice as unknown as DendreoSyncInvoiceRequest;
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  const invalidateComments = useMemo(
    () => () =>
      queryClient.invalidateQueries({
        queryKey: [
          'get-comments',
          invoiceRecord.id,
          'Dendreo::InvoiceSyncState',
        ],
      }),
    [invoiceRecord.id, queryClient],
  );

  const { mutateAsync: addComment, isLoading: isAddingComment } =
    useAddComment(invalidateComments);

  const { mutateAsync: updateIgnore, isLoading: isUpdating } = useMutation({
    mutationKey: ['ignore-invoice-sync', invoiceRecord.id],
    mutationFn: async () => {
      invoiceRecord.markedIgnored = true;
      await invoiceRecord.save();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['DendreoSyncInvoiceRequests'],
      });
    },
  });

  const { mutateAsync: unignore, isLoading: isUnignoring } = useMutation({
    mutationKey: ['unignore-invoice-sync', invoiceRecord.id],
    mutationFn: async () => {
      invoiceRecord.markedIgnored = false;
      await invoiceRecord.save();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['DendreoSyncInvoiceRequests'],
      });
    },
  });

  const onConfirm = useCallback(
    async (reason: string) => {
      await addComment({
        commentableId: invoiceRecord.id,
        content: reason,
        commentableType: 'Dendreo::InvoiceSyncState',
      });
      await updateIgnore();
      setIsOpen(false);
    },
    [addComment, updateIgnore, invoiceRecord.id],
  );

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const Modal = () => (
    <IgnoreInvoiceModal
      isOpen={isOpen}
      onRequestClose={close}
      invoiceNumber={invoiceRecord.number}
      onConfirm={onConfirm}
      isLoading={isAddingComment || isUpdating}
    />
  );

  return {
    open,
    close,
    Modal,
    isIgnored: !!invoiceRecord.markedIgnored,
    unignore: () => unignore(),
    isUnignoring,
  };
};
